import { useState } from 'react';
import { Tooltip, OverlayTrigger, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import 'font-awesome/css/font-awesome.min.css';
import {
  faFileExport,
  faFolderPlus,
  faPaperPlane,
  faPaste,
  faShare,
  faSpinner,
  faTrashCan,
  faUserGraduate,
  faEllipsis
} from '@fortawesome/free-solid-svg-icons';
import CustomToast from '../../components/Toast';
import { DashboardProvider, useDashboardContext } from '../dashboard/context';
import ActivityAssignModal from './components/ActivityAssignModal';
import ShareMenuModal from './components/ActivitiesFloatingMenuModals/ActivitiesShareMenuModal';
import { useActivityContext } from './context';
import MoveMenuModal from '../assignments/components/assignmentFloatingMenu/modals/MoveMenuModal';
import ActivityTypes from '../../enums/activityTypes';
import DeleteMenuModal from '../assignments/components/assignmentFloatingMenu/modals/DeleteMenuModal';
import PublishMenuModal from '../assignments/components/assignmentFloatingMenu/modals/PublishMenuModal';
import { useExportActivitiesMutation, useValidatePublishMutation } from '../../features/authentication/activityApiSlice';
import DuplicateMenuModal from '../assignments/components/assignmentFloatingMenu/modals/DuplicateMenuModal';
import useFloatingMenuVisibility from '../../components/floatingMenu/useFloatingMenuVisibility';

const ActivitiesFloatingMenu = () => {
  const [loadingMenuItemId, setLoadingMenuItemId] = useState(null);
  const [assignModel, setAssignModel] = useState(false);
  const [shareAssignmentsModal, setShareAssignmentsModal] = useState(false);
  const dashboardContext = useDashboardContext();
  const [moveActivityModal, setMoveActivityModal] = useState(false);
  const [publishActivityModal, setPublishActivityModal] = useState(false);
  const [duplicateActivitiesModal, setDuplicateActivitiesModal] = useState(false);
  const context = useActivityContext();
  const [exportActivities, {isLoading: isLoading}] = useExportActivitiesMutation();
  const [validateActivityForPublish] = useValidatePublishMutation();
  const [deleteAssignmentsModal, setDeleteAssignmentsModal] = useState();

  const floatingMenu = [
    {
      id: 1,
      name: 'Assign',
      tooltip: 'Assign flashcards to multiple students and/or yourself',
      icon: <FontAwesomeIcon icon={faUserGraduate} />,
      modal: () => setAssignModel(!assignModel),
      comingSoon: false
    },
    {
      id: 2,
      name: 'Move',
      tooltip: 'Move flashcards to another folder',
      icon: <FontAwesomeIcon icon={faFolderPlus} />,
      modal: () => setMoveActivityModal(!moveActivityModal),
      comingSoon: false
    },
    {
      id: 3,
      name: 'Export',
      tooltip: 'Export flashcards for sharing',
      icon: <FontAwesomeIcon icon={faFileExport} />,
      comingSoon: false
    },
    {
      id: 4,
      name: 'Send Copy',
      tooltip: 'Share flashcards with other teachers',
      icon: <FontAwesomeIcon icon={faShare} />,
      modal: () => setShareAssignmentsModal(!shareAssignmentsModal),
      comingSoon: false
    },
    {
      id: 5,
      name: 'Publish',
      tooltip: 'Publish a flashcard as a Public Flashcard (only one can be checked)',
      icon: <FontAwesomeIcon icon={faPaperPlane} />,
      comingSoon: false,
      modal: () => setPublishActivityModal(!publishActivityModal)
    },
    {
      id: 6,
      name: 'Duplicate',
      tooltip: 'Duplicate a flashcard',
      icon: <FontAwesomeIcon icon={faPaste} />,
      comingSoon: false,
      modal: () => setDuplicateActivitiesModal(!duplicateActivitiesModal)
    },
    {
      id: 7,
      name: 'Delete',
      tooltip: 'Delete flashcards (only unassigned flashcards can be deleted)',
      icon: <FontAwesomeIcon icon={faTrashCan} />,
      modal: () => setDeleteAssignmentsModal(!deleteAssignmentsModal),
      comingSoon: false
    }
  ];

  const { visibleButtons, visibleCountOnScreen, showMoreMenu } = useFloatingMenuVisibility(floatingMenu);

  const floatingMenuHandler = async (value) => {
    if (context.assignedData.length > 0) {
      setLoadingMenuItemId(value.id);
      if (value.id == 3) {
        exportActivitiesAsync();
        return;
      }
      else if (value.id === 4) {
        dashboardContext.setSelectRecipient(true);
        await value.modal();
      }
      else if(value.id == 5){
        if (context.assignedData.length === 1 && !context.isAllSelected) 
          await validateActivityForPublishAsync(value);
        else
          CustomToast('Please select only one flashcard', 'error');
      }
      else if(value.id == 6){
        if(context.selectedCount > 20)
          CustomToast('You can only select up to 20 flashcards', 'warning');
        else
          await value.modal();
      }
      else{
        await value.modal();
      }
      setLoadingMenuItemId(null);
    } else {
      CustomToast('You have not selected any flashcards.', 'info');
    }
  };

  const exportActivitiesAsync = async () => {
    try {

      const option = {
        courseId: context.filters.courseId,
        folderId: context.filters.folderId,
        sortColumn: context.sortColumn ?? null,
        sortDirection: context.sortDirection ?? null,
        assignmentStatus: context.filters.activityStatus,
        assignedVia: [],
        isCollapsed: Boolean(context.filters.isCollapsed),
        searchText: context.filters.selectedKeyWord,
        assignmentIds: context?.isAllSelected ? [-1] : context.assignedData.map((assignment) => assignment.assignmentId)
      };

      const blob = await exportActivities(option).unwrap();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = 'CastleXMLFlashcards.xml';
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
      if(!isLoading)
        setLoadingMenuItemId(null);
    } catch (error) {
      console.log(error);
    }
  };

  const validateActivityForPublishAsync = async (value) => {
    try {
      const options = {
        id: context.assignedData.map((assignment) => {
          return assignment.assignmentId;
        })
      };
      const result = await validateActivityForPublish(options);
      if (!result.error)
        value.modal();
    } catch (ex) {
      console.log(ex);
    }  };

  return (
    <>
      <div className="floating-menu-in">
        {visibleButtons.map((value, index) => (
          <OverlayTrigger
            key={`float-top-${index}`}
            placement="top"
            overlay={
              <Tooltip id={`tooltip-top-${value.id}`} className="custom-tooltip">
                {value.comingSoon ? 'Coming Soon' : (loadingMenuItemId === value.id ? value.name + ' in progress...' : value.tooltip)}
              </Tooltip>
            }>
            <button
              className={!showMoreMenu && index === visibleCountOnScreen - 1 ? 'floating-menu-last-visible' : ''}
              key={`floating-Menu-btn-${index}`}
              id={`floating-menu-${value.id}`}
              onClick={() => !value.comingSoon ? floatingMenuHandler(value) : ''}
            >
              {loadingMenuItemId === value.id ?
                <FontAwesomeIcon icon={faSpinner} spin />
                :
                value.icon
              }
              <span  className={loadingMenuItemId === value.id ? 'inprogress' : ''}>{value.name}</span>
            </button>
          </OverlayTrigger>
        ))}

        {showMoreMenu && <Dropdown>
          <Dropdown.Toggle className='floating-menu-last-visible'>
            <FontAwesomeIcon icon={faEllipsis} />
            More
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {visibleButtons.map((value, index) => (
              <Dropdown.Item
                key={`float-menu-${index}`}
                onClick={() => !value.comingSoon ? floatingMenuHandler(value) : ''}
                disabled={value.comingSoon}>
                {value.icon} {value.name}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>}
      </div>
      {assignModel && (
        <ActivityAssignModal
          modalData={assignModel}
          setModalData={setAssignModel}
        />
      )}

      {shareAssignmentsModal &&
        <ShareMenuModal
          assignmentId={context?.assignedData?.map((item) => item.assignmentId)}
          fromMoreAction={false}
          isModalOpen={setShareAssignmentsModal}
        />
      }
      {moveActivityModal && (
        <MoveMenuModal
          modalData={moveActivityModal}
          setModalData={setMoveActivityModal}
          fromMoreActions={false}
          activityType={ActivityTypes.FlashCard}
        />
      )}

      {deleteAssignmentsModal && (
        <DeleteMenuModal
          modalData={deleteAssignmentsModal}
          setModalData={setDeleteAssignmentsModal}
          fromMoreActions={false}
          activityType={ActivityTypes.FlashCard}
        />
      )}
      <PublishMenuModal
        modalData={publishActivityModal}
        setModalData={setPublishActivityModal}
        fromMoreActions={false}
        activityType={ActivityTypes.FlashCard}
      />
      {duplicateActivitiesModal &&
        <DuplicateMenuModal
          modalData={duplicateActivitiesModal}
          setModalData={setDuplicateActivitiesModal}
          fromMoreActions={false}
          activityType={ActivityTypes.FlashCard}
        />
      }
    </>
  );
};

const Layout = () => {
  return (
    <DashboardProvider>
      <ActivitiesFloatingMenu />
    </DashboardProvider>
  );
};

export default Layout;
